import React, { useState, useEffect } from 'react';
import { G4sLayout } from '../../components/gestion4sat/G4sLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { ChevronLeft, MapPin, Cpu, Building2, Car, Wrench, ExternalLink, Calendar, User, Image, Gauge } from 'lucide-react';
import { getServicio, getFotoUrl } from '../../lib/gestion4satApi';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const tipoLabels = {
  instalacion: 'Instalaci\u00f3n', mantencion: 'Mantenci\u00f3n',
  asistencia: 'Asistencia', revision: 'Revisi\u00f3n',
  retiro: 'Retiro', otro: 'Otro',
};
const tipoColors = {
  instalacion: 'bg-green-500/10 text-green-600', mantencion: 'bg-blue-500/10 text-blue-600',
  asistencia: 'bg-purple-500/10 text-purple-600', revision: 'bg-amber-500/10 text-amber-600',
  retiro: 'bg-red-500/10 text-red-600', otro: 'bg-gray-500/10 text-gray-600',
};
const facturacionColors = {
  pendiente: 'bg-yellow-500/10 text-yellow-700', en_proceso: 'bg-blue-500/10 text-blue-600',
  facturado: 'bg-green-500/10 text-green-600',
};

export default function G4sServicioDetallePage() {
  const { servicioId } = useParams();
  const [servicio, setServicio] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try { setServicio(await getServicio(servicioId)); }
      catch (e) { toast.error(e.message); }
      setLoading(false);
    };
    load();
  }, [servicioId]);

  if (loading) return <G4sLayout><Skeleton className="h-96 w-full" /></G4sLayout>;
  if (!servicio) return <G4sLayout><p className="text-center py-12 text-muted-foreground">Servicio no encontrado</p></G4sLayout>;

  const s = servicio;

  return (
    <G4sLayout>
      <div className="space-y-6 max-w-3xl" data-testid="g4s-servicio-detalle">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/gestion4sat/servicios')}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight">Servicio</h1>
              <Badge className={tipoColors[s.tipo] || tipoColors.otro}>{tipoLabels[s.tipo] || s.tipo}</Badge>
              <Badge className={facturacionColors[s.estado_facturacion] || ''}>{s.estado_facturacion}</Badge>
            </div>
            <p className="text-xs text-muted-foreground font-mono">{s.id}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Datos del servicio */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Wrench className="h-4 w-4 text-emerald-500" /> Datos del Servicio</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>Fecha: <strong>{s.fecha_servicio}</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span>T&eacute;cnico: <strong>{s.tecnico_nombre}</strong></span>
              </div>
              {s.tipo === 'otro' && s.tipo_otro_texto && (
                <div className="text-xs bg-muted/50 p-2 rounded">Tipo: {s.tipo_otro_texto}</div>
              )}
              {s.tipo === 'retiro' && s.destino_retiro && (
                <div>Destino retiro: <Badge variant="outline">{s.destino_retiro}</Badge></div>
              )}
              {s.es_reasignacion && (
                <div className="p-2 rounded bg-orange-500/10 border border-orange-500/20 text-xs">
                  Reasignaci&oacute;n: desde <strong>{s.empresa_anterior_razon}</strong>
                </div>
              )}
              {s.notas && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Notas</div>
                  <p className="bg-muted/50 p-2 rounded text-xs">{s.notas}</p>
                </div>
              )}
              <div className="text-xs text-muted-foreground pt-2 border-t">
                Creado: {new Date(s.created_at).toLocaleString('es-CL')}
              </div>
            </CardContent>
          </Card>

          {/* Dispositivo + Empresa */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Cpu className="h-4 w-4 text-blue-500" /> Dispositivo</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                {s.imei && <div className="font-mono text-xs">IMEI: <strong>{s.imei}</strong></div>}
                {s.patente_formateada && <div className="font-mono">Patente: <strong className="text-lg">{s.patente_formateada}</strong></div>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4 text-emerald-500" /> Empresa</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                <div className="font-medium">{s.empresa_razon_social || 'Sin empresa asignada'}</div>
                {s.empresa_rut && <div className="text-xs font-mono text-muted-foreground">{s.empresa_rut}</div>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Car className="h-4 w-4 text-indigo-500" /> Veh&iacute;culo</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                {s.vehiculo_marca && <div>Marca: <strong>{s.vehiculo_marca}</strong></div>}
                {s.vehiculo_modelo && <div>Modelo: <strong>{s.vehiculo_modelo}</strong></div>}
                {s.vehiculo_anio && <div>A&ntilde;o: {s.vehiculo_anio}</div>}
                {s.vehiculo_tipo && <div>Tipo: {s.vehiculo_tipo}</div>}
                {s.vehiculo_color && <div>Color: {s.vehiculo_color}</div>}
                {s.kilometraje && <div>Kilometraje: <strong>{Number(s.kilometraje).toLocaleString('es-CL')} km</strong></div>}
                {!s.vehiculo_marca && !s.vehiculo_modelo && <span className="text-muted-foreground">Sin datos de veh&iacute;culo</span>}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* GPS */}
        {s.coordenadas?.lat && (
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MapPin className="h-5 w-5 text-green-500" />
                <div>
                  <div className="text-sm font-medium">Ubicaci&oacute;n GPS del servicio</div>
                  <div className="text-xs font-mono text-muted-foreground">{s.coordenadas.lat.toFixed(6)}, {s.coordenadas.lng.toFixed(6)}</div>
                </div>
              </div>
              <Button
                variant="outline" size="sm"
                onClick={() => window.open(`https://www.google.com/maps?q=${s.coordenadas.lat},${s.coordenadas.lng}`, '_blank')}
                data-testid="btn-open-map"
              >
                <ExternalLink className="h-4 w-4 mr-1" /> Abrir en Maps
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Fotos */}
        {s.fotos?.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Image className="h-4 w-4 text-purple-500" /> Fotos del servicio ({s.fotos.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {s.fotos.map(f => (
                  <a key={f.id} href={getFotoUrl(f.filename)} target="_blank" rel="noopener noreferrer" className="block">
                    <img src={getFotoUrl(f.filename)} alt="Foto servicio" className="w-full h-32 object-cover rounded-lg hover:opacity-90 transition-opacity" />
                    <div className="text-xs text-muted-foreground mt-1">{f.subido_por} - {new Date(f.created_at).toLocaleDateString('es-CL')}</div>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </G4sLayout>
  );
}
